import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Typography,
} from 'antd';

import {
  DemandBaseline,
  emptyDemandBaseline,
  emptyRateInfo,
  RateInfo,
  UtilityAccount,
} from '@totem/components/portfolio/buildings/details/utilities/types';
import UtilityAccountsContext from '@totem/components/portfolio/buildings/details/utilities/UtilityAccountsContext';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { BUILDINGS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';

const { Text } = Typography;

const styles = {
  formItem: {
    paddingBottom: '0',
    marginBottom: '1rem',
    width: '100%',
  },
  halfWidth: {
    paddingBottom: '0',
    marginBottom: '1rem',
    width: '48%',
  },
  row: {
    display: 'flex',
    gap: '4%',
  },
};

type FormState = {
  accountNumber: string;
  label: string;
  utilityProvider: string;
  tariffSchedule: string;
  rateInfo: RateInfo;
  demandBaseline: DemandBaseline;
};

const emptyForm = (): FormState => ({
  accountNumber: '',
  label: '',
  utilityProvider: '',
  tariffSchedule: '',
  rateInfo: emptyRateInfo(),
  demandBaseline: emptyDemandBaseline(),
});

type Props = {
  open: boolean;
  account?: UtilityAccount;
  onClose: () => void;
};

const UtilityAccountModal = ({ open, account, onClose }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const { buildingId, requestRefresh } = useContext(UtilityAccountsContext);
  const [isSending, setIsSending] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm());

  const isEditing = !!account;

  useEffect(() => {
    if (open) {
      setForm(
        account
          ? {
              accountNumber: account.accountNumber,
              label: account.label,
              utilityProvider: account.utilityProvider ?? '',
              tariffSchedule: account.tariffSchedule ?? '',
              rateInfo: { ...emptyRateInfo(), ...account.rateInfo },
              demandBaseline: {
                ...emptyDemandBaseline(),
                ...account.demandBaseline,
              },
            }
          : emptyForm(),
      );
    }
  }, [open, account]);

  const setRateInfo = (patch: Partial<RateInfo>) => {
    setForm((prev) => ({ ...prev, rateInfo: { ...prev.rateInfo, ...patch } }));
  };

  const setDemandBaseline = (patch: Partial<DemandBaseline>) => {
    setForm((prev) => ({
      ...prev,
      demandBaseline: { ...prev.demandBaseline, ...patch },
    }));
  };

  const handleSubmit = useCallback(() => {
    if (!form.label.trim() || !form.accountNumber.trim()) {
      return;
    }
    if (isSending) {
      return;
    }

    setIsSending(true);

    const url = isEditing
      ? `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/utility-accounts/${account.id}`
      : `${BUILDINGS_ENDPOINT}/${buildingId}/optimize/utility-accounts`;

    const method = isEditing ? 'PATCH' : 'POST';

    fetch(url, {
      method,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(form),
    })
      .then((res) => {
        CheckResponseShowError(res);

        requestRefresh();

        onClose();
      })
      .finally(() => setIsSending(false));
  }, [
    form,
    isSending,
    isEditing,
    buildingId,
    account,
    getApiToken,
    requestRefresh,
    onClose,
  ]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={600}
      title={
        <ModalTitle>
          {isEditing ? 'Edit Utility Account' : 'Add Utility Account'}
        </ModalTitle>
      }
      footer={
        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="primary"
            loading={isSending}
            disabled={!form.label.trim() || !form.accountNumber.trim()}
            onClick={handleSubmit}
          >
            {isEditing ? 'Save' : 'Add'}
          </Button>
        </ModalFooter>
      }
    >
      <Form layout="vertical">
        <div style={styles.row}>
          <Form.Item label="Account Number" style={styles.halfWidth} required>
            <Input
              value={form.accountNumber}
              onChange={(evt) =>
                setForm({ ...form, accountNumber: evt.target.value })
              }
              placeholder="e.g. 1234567890"
            />
          </Form.Item>
          <Form.Item label="Label" style={styles.halfWidth} required>
            <Input
              value={form.label}
              onChange={(evt) => setForm({ ...form, label: evt.target.value })}
              placeholder="e.g. Main Electric"
            />
          </Form.Item>
        </div>

        <div style={styles.row}>
          <Form.Item label="Utility Provider" style={styles.halfWidth}>
            <Input
              value={form.utilityProvider}
              onChange={(evt) =>
                setForm({ ...form, utilityProvider: evt.target.value })
              }
              placeholder="e.g. Con Edison"
            />
          </Form.Item>
          <Form.Item label="Tariff Schedule" style={styles.halfWidth}>
            <Input
              value={form.tariffSchedule}
              onChange={(evt) =>
                setForm({ ...form, tariffSchedule: evt.target.value })
              }
              placeholder="e.g. SC-9"
            />
          </Form.Item>
        </div>

        <Divider plain>
          <Text type="secondary">Rate Information</Text>
        </Divider>

        <div style={styles.row}>
          <Form.Item label="Energy Rate ($/kWh)" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={4}
              value={form.rateInfo.energyOnlyRate}
              onChange={(val) => setRateInfo({ energyOnlyRate: val ?? 0 })}
            />
          </Form.Item>
          <Form.Item label="Demand Rate ($/kW)" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={4}
              value={form.rateInfo.demandRate}
              onChange={(val) => setRateInfo({ demandRate: val ?? 0 })}
            />
          </Form.Item>
        </div>

        <div style={styles.row}>
          <Form.Item
            label="Blended Effective Rate ($/kWh)"
            style={styles.halfWidth}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={4}
              value={form.rateInfo.blendedEffectiveRate}
              onChange={(val) =>
                setRateInfo({ blendedEffectiveRate: val ?? 0 })
              }
            />
          </Form.Item>
          <Form.Item label="Rate Confirmed" style={styles.halfWidth}>
            <Checkbox
              checked={form.rateInfo.rateConfirmed}
              onChange={(evt) =>
                setRateInfo({ rateConfirmed: evt.target.checked })
              }
            >
              Confirmed
            </Checkbox>
          </Form.Item>
        </div>

        <Form.Item label="Rate Notes" style={styles.formItem}>
          <Input
            value={form.rateInfo.notes}
            onChange={(evt) => setRateInfo({ notes: evt.target.value })}
            placeholder="Optional notes about this rate"
          />
        </Form.Item>

        <Divider plain>
          <Text type="secondary">Demand Baseline</Text>
        </Divider>

        <div style={styles.row}>
          <Form.Item label="Historical Avg kW" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.demandBaseline.historicalAvgKw}
              onChange={(val) =>
                setDemandBaseline({ historicalAvgKw: val ?? 0 })
              }
            />
          </Form.Item>
          <Form.Item label="Target Baseline kW" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.demandBaseline.targetBaselineKw}
              onChange={(val) =>
                setDemandBaseline({ targetBaselineKw: val ?? 0 })
              }
            />
          </Form.Item>
        </div>

        <div style={styles.row}>
          <Form.Item label="Alert Threshold kW" style={styles.halfWidth}>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              value={form.demandBaseline.alertThresholdKw}
              onChange={(val) =>
                setDemandBaseline({ alertThresholdKw: val ?? 0 })
              }
            />
          </Form.Item>
          <div style={{ width: '48%' }} />
        </div>
      </Form>
    </Modal>
  );
};

export default UtilityAccountModal;
